// /onboarding/hours — «متى تستقبل المراجعين؟» (board 25 v2 hours--default /
// --closed-day): step 5 of the staff, place and table shapes. One row per day
// from Saturday, a toggle and two time boxes, «نسخ إلى بقية الأيام» on the
// first row. The business app's Iraqi default to start (Saturday–Thursday
// ٩ ص – ٩ م, Friday off). A table adds «مدة الجلسة» (ruling W2, default ٩٠),
// kept in the wizard's memory BEFORE the write so the scheduling PUT carries it.
//
// This submit WRITES the business — the shape step needs its id — through
// the one idempotent writer (data/enroll.ts ensureBusiness: profile POST
// /v1/business once → booking PUT /scheduling with the shape, awaited → the
// first branch's name) and then booking PUT /hours. A retry never creates
// twice. A field profile refused goes back to its own question with the
// server's sentence. States: default · invalid · saving (· error-network /
// error-server through the shell).

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { Chip } from '@/components/Form';
import { sessionStore } from '@/lib/auth';
import { useLayout } from '@/lib/layout';
import { DayRows } from '../components/DayRows';
import { Msg } from '../components/widgets';
import { Wizard } from '../components/Wizard';
import { HOURS, SHELL } from '../copy';
import { putHours } from '../data/api';
import { afterWriting } from '../data/enroll';
import { ONB } from '../data/flow';
import { shapeOf } from '../data/shapes';
import { copyFirstDay, hoursPayload, onboardingStore, toMinutes, useOnboarding } from '../state';
import { useBusinessPreview } from './Business';
import { useEnrollRun } from './useEnrollRun';

export function HoursScreen() {
  const mobile = useLayout() === 'phone';
  const { hours, draft, createdId, type, sittingMin } = useOnboarding();
  const { profile, preview } = useBusinessPreview('hours');
  const [error, setError] = useState<string | null>(null);
  // The business already existed when this screen opened (a fresh visit): the answers are locked.
  // An unwritten business (a failed write, then a reload) stays here to finish it.
  const [existedOnOpen] = useState(() => !!onboardingStore.get().createdId && onboardingStore.get().scheduled);
  const { phase, failure, run } = useEnrollRun((id) => putHours(id, hoursPayload(onboardingStore.get().hours)));

  const authed = sessionStore.isAuthenticated();
  if (!authed) return <Navigate to={ONB.phone} replace />;
  // A written business never re-runs this step: on to the shape step until its minimum is met (R8), else done.
  if (createdId && existedOnOpen) return <Navigate to={afterWriting()} replace />;
  if (!draft.name.trim() || !draft.category || !draft.city.trim()) return <Navigate to={ONB.name} replace />;

  const table = shapeOf(type) === 'table';

  const submit = () => {
    if (phase === 'saving') return;
    const open = hours.days.filter((d) => d.open);
    if (open.length === 0) {
      setError(HOURS.noDay);
      return;
    }
    for (const d of open) {
      const a = toMinutes(d.from);
      const b = toMinutes(d.to);
      if (a == null || b == null || b <= a) {
        setError(HOURS.order);
        return;
      }
    }
    setError(null);
    void run();
  };

  // Any edit clears the validation line (the error card, when up, hides the rows — the retry button is the only way back).
  const touch = () => setError(null);
  const rows = (
    <DayRows
      hours={hours}
      onToggle={(i) => {
        touch();
        onboardingStore.setDay(i, { open: !hours.days[i].open });
      }}
      onTime={(i, k, v) => {
        touch();
        onboardingStore.setDay(i, k === 'from' ? { from: v } : { to: v });
      }}
      onCopy={() => {
        touch();
        onboardingStore.set({ hours: copyFirstDay(hours) });
      }}
      mobile={mobile}
    />
  );

  const saving = phase === 'saving';
  const state = saving ? 'saving' : error ? 'invalid' : 'default';

  return (
    <Wizard
      screen="hours"
      testId="onb-hours"
      state={state}
      list
      question={HOURS.title(profile)}
      sub={HOURS.sub}
      primary={
        <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} loading={saving}>
          {saving ? HOURS.saving : SHELL.continue}
        </PrimaryAction>
      }
      onSubmit={submit}
      error={failure ? { ...failure, onRetry: () => void run() } : null}
      panel={{ phase: 'preview', profile, preview }}
    >
      {mobile ? rows : <div className="onb-listcard">{rows}</div>}
      {table && (
        <div className="onb-sitting" data-testid="onb-sitting" role="group" aria-label={HOURS.sitting}>
          <span className="onb-sitting-label">{HOURS.sitting}</span>
          <div className="onb-sitting-opts">
            {HOURS.sittingOptions.map((o) => (
              <Chip key={o.min} selected={sittingMin === o.min} onClick={() => onboardingStore.set({ sittingMin: o.min })}>
                {o.label}
              </Chip>
            ))}
          </div>
        </div>
      )}
      {error && <Msg testId="onb-hours-error">{error}</Msg>}
    </Wizard>
  );
}
